/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { debounce, later } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { compact } from '@linc-technologies/linced-common/utils/arrays';
import State from 'linced-app/enums/enrolment-request-state';
import Resource from 'linced-app/enums/enrolment-resource';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import config from 'linced-app/config/environment';

export default class BaseController extends Controller {
  // Services
  @service
  fetch;

  @service
  options;

  @service
  paperToaster;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  students;

  // _resultModel should be set by subclasses to override which entity to fetch.
  _resultModel = 'enrolments/enrolment';

  @optionAlias('nzMOE:schoolType:type')
  schoolType;

  @tracked
  uploadDialogOpen = false;
  @tracked
  uploadSelected = [];
  @tracked
  uploadResults = [];
  @tracked
  uploadInProgress = false;
  uploadStatus = null;
  @tracked uploadNumComplete = 0;
  @tracked
  uploadComplete = false;
  @tracked
  uploadCancelled = false;

  @computed('uploadSelected.length', 'uploadNumComplete')
  get uploadProgressPercent() {
    if (this.uploadSelected.length) {
      return (this.uploadNumComplete / this.uploadSelected) * 100;
    }
    return 0;
  }

  updateUploadStatus() {
    if (isEmpty(this.uploadStatus)) {
      // If we don't have a Status record, we can't poll it for updates.
      return;
    }
    this.uploadStatus.reload().then((status) => {
      let params = {
        jurisdiction: Jurisdiction.NZ_MOE,
        statusId: status.id,
      };
      let numProcessed = status.processed || 0;
      switch (status.status) {
        case State.READY:
        case State.FAILED:
          // Processing complete (or cancelled) - fetch the result records.
          this.store.query(this._resultModel, params).then((results) => {
            this.uploadResults = results;
            this.processUploadResults(results);
          });
          // Update pending upload counts.
          this.students.updatePendingUploads();
          break;

        case State.PROCESSING:
          // Still processing - update progress and queue another update.
          if (numProcessed > this.uploadNumComplete) {
            // Additional requests have been processed since last check.
            // Update the list of results, to display the new ones.
            this.store.query(this._resultModel, params).then((results) => {
              this.uploadResults = results;
            });
          }
          this.uploadNumComplete = status.processed || 0;
          this.pollTimer = debounce(this, 'updateUploadStatus', 1000, false);
          break;
      }
    });
  }

  // performBulkUpload should be overridden by the subclass to implement
  // the actual bulk upload process.
  performBulkUpload() {}

  // processUploadResults should be overridden by the subclass to implement
  // relevant logic for handling the list of responses.
  processUploadResults() {}

  // markUploaded should be overridden by the subclass to implement
  // relevant logic for marking a single response as uploaded successfully.
  markUploaded() {}

  // removeNSN removes the NSN from the uploaded enrolment/person, as ENROL
  // said that it was invalid / doesn't exist.
  // NOTE: No toasts on this, as it's not something which the user requested/expects.
  removeNSN(result) {
    return result
      .get('person')
      .reload()
      .then((person) => {
        person.get('fields.nzMOE:nsn').set('value', '');
        person.set('jurisdictionalId', '');
        return person.save();
      });
  }

  @tracked
  detailsDialogOpen = false;
  @tracked
  detailsResult = null;
  @tracked
  detailsUpdating = false;
  @tracked
  detailsUpdated = false;
  @tracked
  connectivityTestInProgress = false;
  @tracked
  connectivityTestPassed = false;
  @tracked
  connectivityTestStatus = null;
  @tracked
  connectivityTestResponse = null;

  updateConnectivityTestStatus() {
    if (isEmpty(this.connectivityTestStatus)) {
      // If we don't have a Status record, we can't poll it for updates.
      return;
    }
    this.connectivityTestStatus.reload().then((status) => {
      let resourceId = status.get('resourceIds.firstObject');
      switch (status.status) {
        case State.PROCESSING:
          // Still processing - queue another update.
          this.pollTimer = debounce(
            this,
            'updateConnectivityTestStatus',
            1000,
            false,
          );
          break;

        default:
          // Processing complete - fetch the Ping record if successful.
          if (isEmpty(resourceId)) {
            this.connectivityTestResponse = null;
            this.connectivityTestInProgress = false;
            this.connectivityTestPassed = false;
          } else {
            this.store
              .findRecord('enrolments/ping', resourceId)
              .then((ping) => {
                let connectivityTestPassed = ping.get('nzMoe.dexInfo') === 'OK';
                this.connectivityTestResponse = ping;
                this.connectivityTestInProgress = false;
                this.connectivityTestPassed = connectivityTestPassed;
              });
          }
          break;
      }
    });
  }

  @tracked
  markUploadedDialogOpen = false;
  @tracked
  markUploadedSelected = [];
  @tracked
  markUploadedInProgress = false;

  @tracked
  cancelUploadDialogOpen;

  @tracked
  pollTimer;

  @tracked
  invalidSelected;

  @tracked
  validSelected;

  // performBulkMarkUploaded should be overridden by the subclass to implement
  // relevant logic for bulk marking leavers as uploaded to ENROL.
  performBulkMarkUploaded() {}

  @action
  toggleSelected(prop, entity) {
    let selected = this[prop];
    if (selected.includes(entity)) {
      this[prop] = selected.filter((item) => item !== entity);
    } else {
      this[prop] = [...selected, entity];
    }
  }

  @action
  toggleSelectAll(prop, all) {
    let selected = this[prop];
    if (selected.length < all.length) {
      this[prop] = [...all];
    } else {
      this[prop] = [];
    }
  }

  @action
  upload() {
    this.uploadDialogOpen = true;
    this.uploadSelected = compact(this.validSelected);
    this.uploadInProgress = false;
    this.uploadNumComplete = 0;
    this.uploadComplete = false;
    this.uploadCancelled = false;
    this.uploadResults = [];
    this.connectivityTestInProgress = true;
    this.connectivityTestPassed = false;
    this.connectivityTestResponse = null;

    // Initiate connectivity test, to see if we're ok to upload.
    let params = {
      requesterUsername: this.get('session.user.username'),
      providerId: this.get('session.school.jurisdictionalId'),
    };
    let status = this.store.createRecord('enrolments/status', {
      schoolId: this.session.school,
      requesterId: this.session.person,
      jurisdiction: Jurisdiction.NZ_MOE,
      resource: Resource.PING,
      nzMoe: [params],
    });
    this.connectivityTestStatus = status;
    // Pings should return pretty quickly; poll once a second.
    status.save().then(() => later(this, 'updateConnectivityTestStatus', 1000));
  }

  @action
  confirmUpload() {
    this.uploadInProgress = true;
    this.performBulkUpload();
  }

  @action
  cancelUpload() {
    this.cancelUploadDialogOpen = true;
  }

  @action
  closeCancelUploadDialog() {
    this.cancelUploadDialogOpen = false;
  }

  @action
  confirmCancelUpload() {
    this.cancelUploadDialogOpen = false;
    this.uploadCancelled = true;
    let statusId = this.get('uploadStatus.id');
    if (!isEmpty(statusId)) {
      let url = `${config.APP.apiHost}/enrolments/v4/status/${statusId}/:cancel`;
      this.fetch
        .post(url, {})
        .then(() => {
          this.paperToaster.show('Bulk upload cancelled.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          });
        })
        .catch(() => {
          this.paperToaster.show('Bulk upload cancellation failed.', {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
        });
    }
  }

  @action
  closeUploadDialog() {
    // Refresh the route's model etc.
    this.router.refresh(this.uploadRouteName);
    this.uploadDialogOpen = false;
  }

  @action
  showResultDetail(result) {
    this.detailsDialogOpen = true;
    this.detailsResult = result;
    this.detailsUpdating = false;
    this.detailsUpdated = false;
  }

  @action
  updateDetails(differences) {
    this.detailsUpdating = true;
    this.detailsUpdated = false;
    let personRecord = this.get('detailsResult.person');
    return personRecord
      .reload()
      .then((person) => {
        differences.forEach((difference) => {
          person.get(`fields.${difference.key}`).set('value', difference.enrol);
        });
        return person.save();
      })
      .then(() => {
        this.detailsUpdating = false;
        this.detailsUpdated = true;
      })
      .catch(() => {
        this.detailsUpdating = false;
        this.detailsUpdated = false;

        this.paperToaster.show(
          `Error updating ${personRecord.fullName}'s details.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      });
  }

  @action
  closeDetailsDialog() {
    this.detailsDialogOpen = false;
  }

  @action
  handleMarkUploaded(selected) {
    this.markUploadedDialogOpen = true;
    this.markUploadedSelected = selected;
    this.markUploadedInProgress = false;
  }

  @action
  confirmMarkUploaded() {
    this.markUploadedInProgress = true;
    this.performBulkMarkUploaded();
  }

  @action
  closeMarkUploadedDialog() {
    this.markUploadedDialogOpen = false;
  }

  reset() {
    this.validSelected = [];
    this.invalidSelected = [];
    this.markUploadedSelected = [];
    this.uploadSelected = [];
  }
}
